import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import { nonBlank } from '@karehero/helpers/string';
import type { LiveTranscript, LiveTranscriptStatus } from '@karehero/models';

import { Icon } from '@/components/atoms/Icon/Icon';
import { ComposerCard } from './ComposerCard';

/** Kept as the card's name throughout, so it never reads as the summary. */
const HEADING = 'Live captions';

/** Shown in place of words until some arrive. */
const PLACEHOLDER: Partial<Record<LiveTranscriptStatus, string>> = {
  starting: 'Starting live captions…',
  listening: 'Listening…',
};

/** How many lines of words stay in view. */
const LINES = 6;

/** Height of the fade over the oldest words still in view. */
const FADE_PX = 20;

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const keyframes = {
  '@keyframes liveCaptionCaret': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0 },
  },
  '@keyframes liveCaptionPulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(0.5)', opacity: 0.4 },
  },
};

export interface LiveCaptionCardProps {
  transcript: LiveTranscript;
  /** Their own correction of the words, which stands in for them once made. */
  editedText?: string | null;
  /** Offered once the recording has stopped; omit to offer no edit. */
  onEdit?: (text: string) => void;
}

const hasWords = (transcript: LiveTranscript) =>
  !!transcript.text || !!transcript.interimText;

const heardText = ({ text, interimText }: LiveTranscript) =>
  nonBlank([text, interimText]).join(' ');

/** A coral dot that pulses while the session is listening. */
const LiveDot = ({ isLive }: { isLive: boolean }) => (
  <Box
    aria-hidden
    sx={{
      ...keyframes,
      width: 8,
      height: 8,
      flexShrink: 0,
      borderRadius: '50%',
      bgcolor: isLive ? 'custom.coral.600' : 'text.disabled',
      animation: isLive ? 'liveCaptionPulse 1.6s ease-in-out infinite' : 'none',
      [REDUCED_MOTION]: { animation: 'none' },
    }}
  />
);

/** A blinking caret after the latest guess, while listening. */
const Caret = () => (
  <Box
    component='span'
    aria-hidden
    sx={{
      ...keyframes,
      display: 'inline-block',
      width: 2,
      height: '1em',
      ml: '3px',
      verticalAlign: 'text-bottom',
      bgcolor: 'custom.coral.600',
      animation: 'liveCaptionCaret 1s steps(2, start) infinite',
      [REDUCED_MOTION]: { animation: 'none' },
    }}
  />
);

/** The settled words, then the current guess, lighter until it settles. */
const Words = ({ transcript }: { transcript: LiveTranscript }) => (
  <>
    {transcript.text}
    {transcript.interimText && (
      <Typography component='span' variant='inherit' color='text.disabled'>
        {transcript.text ? ' ' : ''}
        {transcript.interimText}
      </Typography>
    )}
    {transcript.status === 'listening' && <Caret />}
  </>
);

/**
 * Keeps the latest words in view over a fixed number of lines; once there
 * is more than fits, the oldest visible line fades out at the top.
 */
const Window = ({ children }: { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const keepLatestInView = () => {
      el.scrollTo({ top: el.scrollHeight });
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    keepLatestInView();
    // Web fonts arriving reflow the words after the first scroll.
    document.fonts?.ready.then(keepLatestInView).catch(() => undefined);
  }, [children]);

  const fade = isOverflowing
    ? `linear-gradient(to bottom, transparent, black ${FADE_PX}px)`
    : undefined;

  return (
    <Typography
      ref={ref}
      component='div'
      variant='body1'
      aria-live='polite'
      sx={(theme) => ({
        maxHeight: `calc(${LINES} * ${theme.typography.body1.lineHeight} * ${theme.typography.body1.fontSize})`,
        overflowY: 'auto',
        // `anywhere` arrived in iOS 15.4; `break-word` covers the rest.
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        maskImage: fade,
        WebkitMaskImage: fade,
      })}
    >
      {children}
    </Typography>
  );
};

/** Correcting the words before they are sent. */
const Editor = ({
  initial,
  onDone,
}: {
  initial: string;
  onDone: (text: string) => void;
}) => {
  const [text, setText] = useState(initial);
  return (
    <Stack spacing={1}>
      <TextField
        multiline
        fullWidth
        autoFocus
        minRows={2}
        maxRows={LINES}
        value={text}
        onChange={(e) => setText(e.target.value)}
        slotProps={{ htmlInput: { 'aria-label': 'Correct the live captions' } }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size='small'
          disabled={!text.trim()}
          onClick={() => onDone(text.trim())}
        >
          Done
        </Button>
      </Box>
    </Stack>
  );
};

/**
 * LiveCaptionCard shows what a recording is saying as it is said, in a card
 * of its own above the bar. It stays with the recording until it is sent or
 * deleted, and can be corrected before it is sent.
 */
export const LiveCaptionCard = ({
  transcript,
  editedText,
  onEdit,
}: LiveCaptionCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const placeholder = PLACEHOLDER[transcript.status];
  if (!hasWords(transcript) && !placeholder && !editedText) return null;

  const isLive = transcript.status === 'listening';
  const canEdit = !!onEdit && transcript.status === 'ended' && !isEditing;

  const body = isEditing ? (
    <Editor
      initial={editedText ?? heardText(transcript)}
      onDone={(text) => {
        setIsEditing(false);
        onEdit?.(text);
      }}
    />
  ) : editedText ? (
    <Window>{editedText}</Window>
  ) : hasWords(transcript) ? (
    <Window>
      <Words transcript={transcript} />
    </Window>
  ) : (
    <Typography variant='body1' color='text.secondary'>
      {placeholder}
    </Typography>
  );

  return (
    <ComposerCard
      mark={<LiveDot isLive={isLive} />}
      title={HEADING}
      action={
        canEdit && (
          <Button size='small' onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )
      }
    >
      {body}
    </ComposerCard>
  );
};

LiveCaptionCard.displayName = 'LiveCaptionCard';
export default LiveCaptionCard;
